// src/components/TellerList.js
import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import Header from './Header';
import Footer from './Footer';
import { AuthContext } from '../context/AuthContext';
import {
  bookSlot,
  formatDateTime,
  getProfile,
  subscribeTellerAvailability,
  subscribeTellers,
  toNumber,
} from '../services/marketplace';
import '../style/Marketplace.css';
import { useI18n } from '../i18n/I18nContext';
import { getLocalizedError, isPermissionError } from '../i18n/errorCopy';
import { translateSessionMode, translateSessionName } from '../i18n/sessionCopy';
import fallbackTellerImage from '../assets/images/tarot-empress.jpg';

const fallbackReadingTypes = ['All', 'Tarot', 'Astrology', 'Psychic', 'Love', 'Career', 'Dreams'];
const sessionTypes = ['All', 'video', 'chat', 'phone', 'email'];

const readingTypeLabel = (value, t) => ({
  Tarot: t('sessionNames.Tarot reading'),
  Astrology: t('sessionNames.Astrology reading'),
  Psychic: t('sessionNames.Psychic reading'),
  Love: t('sessionNames.Love and relationships'),
  Career: t('sessionNames.Career and purpose'),
  Dreams: t('sessionNames.Dream interpretation'),
}[value] || value);

const TellerList = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, loading: authLoading } = useContext(AuthContext);
  const { t, language: activeLanguage } = useI18n();
  const [tellers, setTellers] = useState([]);
  const [selectedId, setSelectedId] = useState(id || '');
  const [slots, setSlots] = useState([]);
  const [search, setSearch] = useState('');
  const [type, setType] = useState('All');
  const [sessionType, setSessionType] = useState('All');
  const [maxPrice, setMaxPrice] = useState('');
  const [onlineOnly, setOnlineOnly] = useState(false);
  const [language, setLanguage] = useState('All');
  const [loading, setLoading] = useState(true);
  const [slotLoading, setSlotLoading] = useState(false);
  const [error, setError] = useState('');
  const [permissionIssue, setPermissionIssue] = useState(false);
  const [bookingId, setBookingId] = useState('');

  useEffect(() => {
    setSelectedId(id || '');
  }, [id]);

  useEffect(() => {
    if (authLoading) return undefined;
    setLoading(true);
    setError('');
    setPermissionIssue(false);
    const unsubscribe = subscribeTellers(
      (rows) => {
        setTellers(rows);
        setLoading(false);
      },
      (err) => {
        setPermissionIssue(isPermissionError(err));
        setError(getLocalizedError(err, activeLanguage, t('tellers.loadFailed')));
        setLoading(false);
      },
    );
    return unsubscribe;
  }, [activeLanguage, authLoading, user?.uid, t]);

  const dynamicTypes = useMemo(() => {
    const values = new Set(fallbackReadingTypes);
    tellers.forEach((teller) => {
      [...(teller.skills || []), ...(teller.category || [])].forEach((item) => {
        const value = String(item || '').trim();
        if (value) values.add(value);
      });
    });
    return [...values];
  }, [tellers]);

  const languages = useMemo(() => {
    const values = new Set(['All']);
    tellers.forEach((teller) => (teller.languages || []).forEach((item) => {
      const value = String(item || '').trim();
      if (value) values.add(value);
    }));
    return [...values];
  }, [tellers]);

  const filteredTellers = useMemo(() => {
    const q = search.trim().toLowerCase();
    const max = maxPrice === '' ? Infinity : toNumber(maxPrice, Infinity);
    return tellers.filter((teller) => {
      const skills = Array.isArray(teller.skills) ? teller.skills : [];
      const categories = Array.isArray(teller.category) ? teller.category : [];
      const tellerLanguages = Array.isArray(teller.languages) ? teller.languages : [];
      const searchable = [
        teller.name,
        teller.email,
        teller.bio,
        teller.price,
        teller.rating,
        ...skills,
        ...categories,
        ...tellerLanguages,
      ].join(' ').toLowerCase();
      const topicValues = [...skills, ...categories].join(' ').toLowerCase();
      const languageValues = tellerLanguages.join(' ').toLowerCase();
      const matchesSearch = !q || searchable.includes(q);
      const matchesType = type === 'All' || topicValues.includes(type.toLowerCase()) || searchable.includes(type.toLowerCase());
      const matchesLanguage = language === 'All' || languageValues.includes(language.toLowerCase());
      const matchesPrice = toNumber(teller.price) <= max;
      const matchesOnline = !onlineOnly || teller.online;
      return matchesSearch && matchesType && matchesLanguage && matchesPrice && matchesOnline;
    });
  }, [tellers, search, type, language, maxPrice, onlineOnly]);

  const selectedTeller = useMemo(() => {
    const selected = selectedId ? filteredTellers.find((t) => t.id === selectedId || t.userId === selectedId) : null;
    return selected || filteredTellers[0] || null;
  }, [selectedId, filteredTellers]);

  const isOwnTellerProfile = Boolean(user && selectedTeller && (
    selectedTeller.userId === user.uid
    || selectedTeller.id === user.uid
    || String(selectedTeller.email || '').toLowerCase() === String(user.email || '').toLowerCase()
  ));

  useEffect(() => {
    if (!selectedTeller?.userId) {
      setSlots([]);
      return undefined;
    }
    setSlotLoading(true);
    const unsubscribe = subscribeTellerAvailability(
      selectedTeller.userId,
      (rows) => {
        setSlots(rows);
        setSlotLoading(false);
      },
      (err) => {
        setPermissionIssue(isPermissionError(err));
        setError(getLocalizedError(err, activeLanguage, t('tellers.availabilityFailed')));
        setSlotLoading(false);
      },
    );
    return unsubscribe;
  }, [activeLanguage, selectedTeller?.userId, t]);

  const visibleSlots = useMemo(() => {
    return slots.filter((slot) => sessionType === 'All' || slot.sessionType === sessionType);
  }, [slots, sessionType]);

  const hasPermissionIssue = permissionIssue;

  const clearFilters = () => {
    setSearch('');
    setType('All');
    setLanguage('All');
    setSessionType('All');
    setMaxPrice('');
    setOnlineOnly(false);
  };

  const handleBook = async (slot) => {
    if (!user) {
      navigate('/login');
      return;
    }
    if (isOwnTellerProfile) {
      setError(t('tellers.ownError'));
      return;
    }
    setBookingId(slot.id);
    setError('');
    setPermissionIssue(false);
    try {
      const profile = await getProfile(user.uid);
      const booking = await bookSlot({ slot, user, profile, teller: selectedTeller });
      navigate(`/session-payment/${booking.id}`);
    } catch (err) {
      setPermissionIssue(isPermissionError(err));
      setError(getLocalizedError(err, activeLanguage, t('tellers.bookingFailed')));
    } finally {
      setBookingId('');
    }
  };

  return (
    <div>
      <Header />
      <main className="market-page">
        <section className="market-hero compact">
          <div>
            <p className="eyebrow">{t('tellers.live')}</p>
            <h1>{t('tellers.title')}</h1>
            <p>{t('tellers.body')}</p>
          </div>
          <div className="market-search-card">
            <label htmlFor="teller-search">{t('tellers.search')}</label>
            <input id="teller-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder={t('tellers.placeholder')} />
            <div className="filter-row">
              <select value={type} onChange={(e) => setType(e.target.value)} aria-label={t('tellers.readingType')}>
                {dynamicTypes.map((option) => <option key={option} value={option}>{option === 'All' ? t('tellers.all') : readingTypeLabel(option, t)}</option>)}
              </select>
              <select value={language} onChange={(e) => setLanguage(e.target.value)} aria-label={t('tellers.language')}>
                {languages.map((option) => <option key={option} value={option}>{option === 'All' ? t('tellers.allLanguages') : option}</option>)}
              </select>
            </div>
            <div className="filter-row">
              <select value={sessionType} onChange={(e) => setSessionType(e.target.value)} aria-label={t('tellers.sessionType')}>
                {sessionTypes.map((option) => <option key={option} value={option}>{option === 'All' ? t('tellers.allSessions') : translateSessionMode(option, t)}</option>)}
              </select>
              <input type="number" min="1" value={maxPrice} onChange={(e) => setMaxPrice(e.target.value)} placeholder={t('tellers.max')} />
              <label className="check-filter"><input type="checkbox" checked={onlineOnly} onChange={(e) => setOnlineOnly(e.target.checked)} /> {t('tellers.online')}</label>
            </div>
          </div>
        </section>

        {error && (
          <div className="market-alert">
            <strong>{hasPermissionIssue ? t('tellers.permissionTitle') : t('tellers.notice')}</strong>
            <span>{hasPermissionIssue
              ? t('tellers.permissionBody')
              : error}</span>
          </div>
        )}

        <section className="market-layout">
          <aside className="teller-results">
            <div className="section-title-row">
              <h2>{t('tellers.results')}</h2>
              <span>{filteredTellers.length} {t('tellers.shown')}</span>
            </div>
            {loading ? (
              <div className="empty-card">{t('tellers.loading')}</div>
            ) : filteredTellers.length === 0 ? (
              <div className="empty-card teller-empty-state">
                <strong>{tellers.length === 0 ? t('tellers.noApproved') : t('tellers.noMatch')}</strong>
                <span>{tellers.length === 0
                  ? t('tellers.approvalBody')
                  : t('tellers.filterBody')}</span>
                <button type="button" onClick={clearFilters}>{t('tellers.clear')}</button>
              </div>
            ) : (
              <div className="teller-result-list">
                {filteredTellers.map((teller) => (
                  <button
                    key={teller.id}
                    type="button"
                    className={`teller-result-card ${selectedTeller?.id === teller.id ? 'active' : ''}`}
                    onClick={() => setSelectedId(teller.id)}
                  >
                    <img src={teller.photo || fallbackTellerImage} alt={teller.name} onError={(e) => { e.currentTarget.src = fallbackTellerImage; }} />
                    <div>
                      <strong>{teller.name}</strong>
                      <span>{teller.skills?.slice(0, 3).join(' · ') || t('tellers.guidance')}</span>
                      <small>{teller.online ? t('tellers.online') : t('tellers.availableBooking')} · {teller.rating.toFixed(1)} {t('tellers.rating')}</small>
                    </div>
                    <em>€{teller.price}/{t('tellers.perSession')}</em>
                  </button>
                ))}
              </div>
            )}
          </aside>

          <section className="teller-detail-panel">
            {!selectedTeller ? (
              <div className="empty-card">{t('tellers.select')}</div>
            ) : (
              <>
                <div className="profile-cover">
                  <img src={selectedTeller.photo || fallbackTellerImage} alt={selectedTeller.name} onError={(e) => { e.currentTarget.src = fallbackTellerImage; }} />
                  <div>
                    <span className={`status-pill ${selectedTeller.online ? 'online' : ''}`}>{selectedTeller.online ? t('tellers.online') : t('tellers.bookingOpen')}</span>
                    <h2>{selectedTeller.name}</h2>
                    <p>{selectedTeller.bio}</p>
                    <div className="chip-row">
                      {(selectedTeller.skills?.length ? selectedTeller.skills : ['Tarot', 'Clarity']).map((skill) => <span key={skill}>{skill}</span>)}
                    </div>
                    <div className="profile-meta">
                      <strong>€{selectedTeller.price}</strong><span>{t('tellers.perSession')}</span>
                      <strong>{selectedTeller.rating.toFixed(1)}</strong><span>{t('tellers.averageRating')}</span>
                      <strong>{selectedTeller.languages.join(', ')}</strong><span>{t('tellers.languages')}</span>
                    </div>
                  </div>
                </div>

                <div className="availability-panel">
                  <div className="section-title-row">
                    <h3>{t('tellers.available')}</h3>
                    <span>{visibleSlots.length} {t('tellers.openSlots')}</span>
                  </div>
                  {slotLoading ? (
                    <div className="empty-card">{t('tellers.loadingAvailability')}</div>
                  ) : visibleSlots.length === 0 ? (
                    <div className="empty-card">{t('tellers.noSlots')}</div>
                  ) : (
                    <div className="slot-grid">
                      {visibleSlots.map((slot) => (
                        <article className="slot-card" key={`${slot.source}-${slot.id}`}>
                          <div>
                            <strong>{translateSessionName(slot.sessionName, t, t('workflow.dashboard.liveReading'))}</strong>
                            <span>{formatDateTime(slot)}</span>
                            <small>{slot.duration} min · {translateSessionMode(slot.sessionType, t)}</small>
                          </div>
                          <div className="slot-price">€{slot.price.toFixed(2)}</div>
                          <button
                            type="button"
                            onClick={() => handleBook(slot)}
                            disabled={isOwnTellerProfile || bookingId === slot.id}
                            title={isOwnTellerProfile ? t('tellers.ownTitle') : t('tellers.bookTitle')}
                          >
                            {isOwnTellerProfile ? t('tellers.yourSession') : bookingId === slot.id ? t('tellers.booking') : t('tellers.book')}
                          </button>
                        </article>
                      ))}
                    </div>
                  )}
                </div>

                <div className="trust-strip">
                  <div><strong>{t('tellers.secureRoom')}</strong><span>{t('tellers.secureBody')}</span></div>
                  <div><strong>{t('tellers.timing')}</strong><span>{t('tellers.timingBody')}</span></div>
                  <div><strong>{t('tellers.cancellation')}</strong><span>{t('tellers.cancellationBody')}</span></div>
                </div>
              </>
            )}
          </section>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default TellerList;
